import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { FC, ReactElement } from 'react';
import styled from 'styled-components';

import { humanReadableRunTime, startEndTime } from 'dashboard/lib/workflows/run-stats';
import { WorkflowRunStep } from 'dashboard/models/workflow-step';
import { HStack } from 'design-system/components/Stack';
import Stack from 'design-system/components/Stack/Stack';
import { Label, Paragraph } from 'design-system/components/Text/Text';
import Tooltip from 'design-system/components/Tooltip';
import { ApiModel } from 'lib/interfaces/api';

const StyledStack = styled(Stack)`
  width: 100%;
  min-width: 250px;
`;

const CircleIconContainer = styled.div`
  padding: 0 ${Spacing[1]};
`;

const HelpTextContainer = styled.div`
  width: 200px;
`;
const ListItem = styled.div`
  text-align: right;
`;
interface WorkflowRunStepStatsLineItemProps {
  label: string;
  value: string | number | ReactElement;
  helpText: string;
  showHelpText: boolean;
}

const WorkflowRunStepStatsLineItem: FC<WorkflowRunStepStatsLineItemProps> = ({
  label,
  value,
  helpText,
  showHelpText = true,
}) => {
  return (
    <HStack justify='space-between'>
      <Label color='secondary'>
        {label}
        {showHelpText && (
          <Tooltip
            aria-label={`Help text for ${label.toLocaleLowerCase()}`}
            side='bottom'
            iconTrigger={
              <CircleIconContainer>
                <CircleInfoIcon size={12} />
              </CircleIconContainer>
            }
          >
            <HelpTextContainer>
              <Paragraph color='primary' size='sm'>
                {helpText}
              </Paragraph>
            </HelpTextContainer>
          </Tooltip>
        )}
      </Label>
      <div>{value}</div>
    </HStack>
  );
};

interface WorkflowRunStepStatsProps {
  step: WorkflowRunStep;
  showHelpText: boolean;
}

const WorkflowRunStepStats: FC<WorkflowRunStepStatsProps> = ({ step, showHelpText = true }) => {
  const { startTime, endTime } = startEndTime([step]);
  const {
    startTimeString: createTimeString,
    durationString,
    timestampFormat,
  } = humanReadableRunTime(startTime, endTime);
  return (
    <StyledStack>
      <WorkflowRunStepStatsLineItem
        label='Duration'
        value={durationString}
        helpText='Duration from the first time this workflow step was ready to be processed, to the last time it was completed or updated.'
        showHelpText={showHelpText}
      />

      <WorkflowRunStepStatsLineItem
        label='Started'
        value={createTimeString}
        helpText='The first time the system was ready to process this step.'
        showHelpText={showHelpText}
      />

      {step['attributes']['completed-at'] ? (
        <WorkflowRunStepStatsLineItem
          label='Completed'
          value={DateTime.fromISO(step['attributes']['completed-at']).toFormat(timestampFormat)}
          helpText='The time when this workflow step was completed successfully or with error.'
          showHelpText={showHelpText}
        />
      ) : (
        <WorkflowRunStepStatsLineItem
          label='Last updated'
          value={DateTime.fromISO(step['attributes']['updated-at']).toFormat(timestampFormat)}
          helpText='The last time this workflow step was updated.'
          showHelpText={showHelpText}
        />
      )}

      {
        // Retry count for non-action steps is not meaningful, since parent steps and wait steps are retried multiple times as a matter of course.
        step.type === ApiModel.WorkflowRunStepAction &&
          step['attributes']['total-attempts'] > 1 && (
            <WorkflowRunStepStatsLineItem
              label='Retries'
              value={step['attributes']['total-attempts']}
              helpText='The number of times this step was retried.'
              showHelpText={showHelpText}
            />
          )
      }

      {step['attributes']['rate-limited-attempts'] &&
        step['attributes']['rate-limited-attempts'].length > 0 && (
          <WorkflowRunStepStatsLineItem
            label='Rate-limited'
            value={
              <>
                {step['attributes']['rate-limited-attempts'].map((time, i) => {
                  return (
                    <ListItem key={`rate-limited-${i}`}>
                      {DateTime.fromISO(time).toFormat(timestampFormat)}
                    </ListItem>
                  );
                })}
              </>
            }
            helpText='The processing of this step was postponed due to unusually high workflow load from this organization. This likely resulted in delay when processing this step. A step could be rate limited multiple times if the traffic volume remained persistently high.'
            showHelpText={showHelpText}
          />
        )}

      {step['attributes']['last-deprioritized-at'] && (
        <WorkflowRunStepStatsLineItem
          label='Deprioritized'
          value={DateTime.fromISO(step['attributes']['last-deprioritized-at']).toFormat(
            timestampFormat,
          )}
          helpText="The time when this step's priority was downgraded due to unusually high processing time of this step in previous runs. This likely resulted in delay when processing this step."
          showHelpText={showHelpText}
        />
      )}
    </StyledStack>
  );
};

export default WorkflowRunStepStats;
